import React from 'react'
import { FolderOpen, Files } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type BulkFilePickerProps = {
  selectedCount: number
  folderInputRef: React.RefObject<HTMLInputElement> | any
  onFolderSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectionLocked?: boolean
}

export const BulkFilePicker: React.FC<BulkFilePickerProps> = ({
  selectedCount,
  folderInputRef,
  onFolderSelect,
  selectionLocked = false,
}) => {
  const { t } = useTranslation(['BulkPodUpload'])

  const pickerClass =
    'group flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card p-6 text-center cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/20'

  return (
    <div className="space-y-4">
      {!selectionLocked ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button
            type="button"
            className={pickerClass}
            onClick={() => document.getElementById('local-folder-picker')?.click()}
          >
            <FolderOpen className="h-8 w-8 text-primary transition-transform group-hover:scale-105" />
            <span className="text-sm font-medium text-foreground">
              {t('BulkPodUpload:picker.folderTitle', 'Select folder')}
            </span>
            <span className="text-xs text-muted-foreground max-w-[220px]">
              {t('BulkPodUpload:picker.folderHint', 'Import all supported files from a folder recursively')}
            </span>
          </button>

          <button
            type="button"
            className={pickerClass}
            onClick={() => document.getElementById('local-files-picker')?.click()}
          >
            <Files className="h-8 w-8 text-primary transition-transform group-hover:scale-105" />
            <span className="text-sm font-medium text-foreground">
              {t('BulkPodUpload:picker.filesTitle', 'Select files')}
            </span>
            <span className="text-xs text-muted-foreground max-w-[220px]">
              {t('BulkPodUpload:picker.filesHint', 'Pick specific PDF, PNG, or JPG files')}
            </span>
          </button>
        </div>
      ) : null}

      {selectedCount > 0 ? (
        <div className="flex items-center justify-between rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-4 py-3 text-sm">
          <span className="font-medium text-emerald-700 dark:text-emerald-400">
            {t('BulkPodUpload:picker.selectedCount', '{{count}} files ready to upload', {
              count: selectedCount,
            })}
          </span>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {t('BulkPodUpload:picker.empty', 'No files selected yet')}
        </div>
      )}

      {!selectionLocked ? (
        <>
          <input
            type="file"
            id="local-folder-picker"
            ref={folderInputRef}
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onFolderSelect}
          />
          <input
            type="file"
            id="local-files-picker"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onFolderSelect}
          />
        </>
      ) : null}
    </div>
  )
}

export default BulkFilePicker
